import { useState, useRef, ChangeEvent } from "react";
import { Button } from "@/components/ui/button";
import { Upload, X } from "lucide-react";
import Image from "next/image";

interface ImageUploadProps {
  onFileSelect: (file: File) => void;
  previewUrl: string | null;
  maxSize?: number; // in MB
  className?: string;
  aspectRatio?: string; // e.g., "1:1", "16:9", "4:3", etc.
}

export default function ImageUpload({
  onFileSelect,
  previewUrl,
  maxSize = 2,
  className = "h-40 w-40",
  aspectRatio = "1:1",
}: ImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndProcessFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndProcessFile(e.target.files[0]);
    }
  };

  const validateAndProcessFile = (file: File) => {
    // Clear previous errors
    setError(null);
    
    // Check file type
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validImageTypes.includes(file.type)) {
      setError(`Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.`);
      return;
    }
    
    // Check file size
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > maxSize) {
      setError(`File too large. Maximum size is ${maxSize}MB.`);
      return;
    }
    
    // Pass the file to the parent component
    onFileSelect(file);
  };

  const removeImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileSelect(new File([""], "empty", { type: "text/plain" }));
  };

  // Calculate the style based on aspect ratio
  let aspectRatioStyle = {};
  if (aspectRatio) {
    const [width, height] = aspectRatio.split(':').map(Number);
    aspectRatioStyle = {
      aspectRatio: `${width}/${height}`,
    };
  }

  return (
    <div className="space-y-2">
      <div
        className={`relative ${className} flex items-center justify-center border-2 border-dashed rounded-md transition-colors ${
          isDragging ? "border-primary bg-primary/10" : "border-gray-300 hover:border-primary/50"
        } ${error ? "border-red-500" : ""}`}
        style={aspectRatioStyle}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {previewUrl ? (
          <>
            <img
              src={previewUrl}
              alt="Uploaded image"
              className="object-cover w-full h-full rounded-md"
            />
            <button
              type="button"
              onClick={removeImage}
              className="absolute top-2 right-2 p-1 rounded-full bg-black/70 text-white hover:bg-black/90"
            >
              <X className="h-4 w-4" />
            </button>
          </>
        ) : (
          <div className="text-center p-4">
            <Upload className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm font-medium">
              Drag & drop or click to upload
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Max size: {maxSize}MB
            </p>
          </div>
        )}
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </div>
      
      {error && (
        <p className="text-xs font-medium text-red-500">{error}</p>
      )}
    </div>
  );
} 